import React, { useMemo, useRef } from "react";
import { useState, useEffect } from "react";
import { IoSearchOutline } from "react-icons/io5";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import useClickOutSideComponent from "../../../hooks/useClickOutsideComponent";

interface State {
  name: string;
}

interface StatesResponse {
  data: {
    name: string;
    states: State[];
  };
}

interface SelectStateProps {
  country: string;
  onSelect: (state: string) => void;
  value?: string;
  labelClassName?: string;
  labelText?: string;
  isRequired?: boolean;
  error?: string;
  isDropdownAbove?: boolean;
}

const statesUrl = `https://countriesnow.space/api/v0.1/countries/states`;

const StateDropdown: React.FC<SelectStateProps> = ({
  onSelect,
  country,
  value,
  error,
  labelClassName,
  labelText,
  isRequired,
  isDropdownAbove,
}) => {
  const [states, setStates] = useState<State[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [selectedState, setSelectedState] = useState<string>("");
  const containerRef = useRef<HTMLDivElement | null>(null);

  useClickOutSideComponent(containerRef, () => setShowDropdown(false));

  const statesFiltered = useMemo(() => {
    return states.filter((c) =>
      c.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [states, search]);

  const handleSelect = (state: string) => {
    setSelectedState(state);
    setShowDropdown(false);
    onSelect(state);
  };

  useEffect(() => {
    if (!country) {
      setStates([]);
      setSelectedState("");
      setSearch("");
      return;
    }

    const fetchStates = async () => {
      setLoading(true);
      setFetchError(null);
      setSelectedState("");
      setSearch("");

      try {
        const response = await fetch(statesUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ country }),
        });

        if (!response.ok) throw new Error("Failed to fetch states");

        const result: StatesResponse = await response.json();

        if (result?.data?.states) {
          setStates(result.data.states);
        } else {
          setStates([]);
        }
      } catch (err) {
        console.error("Error fetching states:", err);
        setFetchError("Unable to load states");
      } finally {
        setLoading(false);
      }
    };

    fetchStates();
  }, [country]);

  useEffect(() => {
    if (value) {
      setSelectedState(value);
    }
  }, [value]);

  const disabled = !country;

  return (
    <div className='relative mt-1' ref={containerRef}>
      {labelText && (
        <label
          className={` font-medium capitalize text-sm ${
            showDropdown ? "text-blueShades-4" : "text-ashShade-16"
          } ${labelClassName}`}
        >
          {labelText} {isRequired && <span className='text-ERROR'>*</span>}
        </label>
      )}
      <div
        className={`border cursor-pointer  ${showDropdown ? "border-[1.5px] border-blueShades-6 outline outline-[3px] outline-[#D8E9F080]" : error ? "border-[1.5px] border-ERROR outline outline-3px outline-[#FFE5E5]" : "border-blueShades-2"} rounded-md px-3 py-2 text-sm text-ashShade-16`}
        onClick={() => {
          if (!disabled) setShowDropdown(!showDropdown);
        }}
      >
        {selectedState ? (
          <div className='flex items-center justify-between'>
            <span className='text-sm'>{selectedState}</span>
            {showDropdown ? (
              <IoIosArrowUp size={16} className='text-ashShade-2' />
            ) : (
              <IoIosArrowDown size={16} className='text-ashShade-2' />
            )}
          </div>
        ) : (
          <p className='text-bash !font-SatoshiRegular text-base w-full justify-between flex items-center'>
            <span className='text-sm'>Select State</span>
            <span>
              {showDropdown ? (
                <IoIosArrowUp size={16} className='text-ashShade-2' />
              ) : (
                <IoIosArrowDown size={16} className='text-ashShade-2' />
              )}
            </span>
          </p>
        )}
      </div>

      {!disabled && showDropdown && (
        <div
          className={`absolute bg-white border rounded-md w-full z-10 shadow-md ${isDropdownAbove ? "bottom-full" : "top-full"}`}
        >
          <div className='py-2 px-3 border border-ashShade-0 sticky right-0 left-0'>
            <div className='relative w-full'>
              <IoSearchOutline
                size={16}
                className='absolute left-3 top-[55%] -translate-y-1/2 text-bash'
              />
              <input
                type='text'
                placeholder='Search'
                className='w-full border bg-[#fcfcfd]  border-ashShade-0 rounded-md pl-8 pr-2 py-1 outline-none placeholder:text-bblack-1 placeholder:font-medium placeholder:text-sm'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className='max-h-52 overflow-y-auto'>
            {statesFiltered.map((state) => (
              <div
                key={state.name}
                className={`py-2 px-3 flex items-center  hover:bg-bbg cursor-pointer font-SatoshiRegular text-base ${selectedState === state.name && "bg-ashShade-13"}`}
                onClick={() => handleSelect(state.name)}
              >
                {state.name}
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <span className='absolute left-0 top-full text-ERROR text-[13px] mb-4'>
          {error}
        </span>
      )}
    </div>
  );
};

export default StateDropdown;
